import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import { GrupoDeEscolha } from '../../../../components/GrupoDeEscolha'
import { CampoDeCaixa, CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../../../components/campos'
import { TEXTOS } from '../../../../lib/glossario'
import type { ParecerDaIa } from '../../../../types/chamado'
import { competenciaPorExtenso } from '../../competencia'
import type { FormularioDeMarcacao } from './esquema'
import { OPCOES_ACEITE, OPCOES_CONFORMIDADE, OPCOES_CRITICIDADE, OPCOES_NATUREZA, OPCOES_PRAZO } from './opcoes'
import { SugestaoDaIa } from './SugestaoDaIa'

interface Props {
  register: UseFormRegister<FormularioDeMarcacao>
  errors: FieldErrors<FormularioDeMarcacao>
  /** Quando corretiva, aparece a criticidade — é ela que define o prazo do incidente. */
  corretiva: boolean
  /** Mês de referência do chamado (`AAAA-MM`), ou nulo enquanto ele estiver aberto. */
  competencia: string | null
  /** O parecer exibido, para a sugestão da IA aparecer colada em cada pergunta. */
  parecer: ParecerDaIa | null
  /** O que está marcado agora, para a etiqueta dizer se a sugestão foi mantida. */
  valores: Partial<FormularioDeMarcacao>
}

/** Marcar o chamado como fora do mês esconde as quatro perguntas: ele não será medido. */
function entraNoMes(valores: Partial<FormularioDeMarcacao>): boolean {
  return valores.contaNoMes !== false
}

/**
 * As perguntas do julgamento, na ordem em que uma depende da outra.
 *
 * A ordem não é estética. O serviço prestado decide o prazo aplicável e decide para qual indicador
 * o chamado vai — por isso ele vem antes da pergunta sobre prazo. Conformidade e aceite são
 * perguntadas sempre, corretiva ou não.
 *
 * @param props o `register` do formulário, os erros e se o serviço é corretivo
 * @returns os campos
 */
export function CamposDoJulgamento({ register, errors, corretiva, competencia, parecer, valores }: Props) {
  const t = TEXTOS.marcacao

  return (
    <div className="space-y-5">
      {/* Onde esta análise vai contar, antes de qualquer pergunta. Num chamado aberto ela não conta
          em lugar nenhum ainda, e descobrir isso depois de analisar dezenas deles é o pior momento
          possível. */}
      <p
        className={
          competencia
            ? 'rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700'
            : 'max-w-prose rounded-lg border border-laranja-200 bg-laranja-50 px-3 py-2 text-sm text-slate-800'
        }
      >
        {competencia ? (
          <>
            {t.ondeConta.fechado} <strong className="font-semibold">{competenciaPorExtenso(competencia)}</strong>.
          </>
        ) : (
          t.ondeConta.aberto
        )}
      </p>

      <CampoDeCaixa rotulo={t.contaNoMes.rotulo} ajuda={t.contaNoMes.ajuda} {...register('contaNoMes')} />

      {!entraNoMes(valores) && (
        <p role="note" className="max-w-prose rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {t.foraDaApuracao.aviso}
        </p>
      )}

      {entraNoMes(valores) && (
        <>
      <div>
        <GrupoDeEscolha
          legenda={t.natureza.rotulo}
          ajuda={t.natureza.ajuda}
          erro={errors.natureza?.message}
          opcoes={OPCOES_NATUREZA}
          {...register('natureza')}
        />
        <SugestaoDaIa parecer={parecer} campo="natureza" valorAtual={valores.natureza} />
      </div>

      {corretiva && (
        <div>
          <CampoDeSelecao rotulo={t.criticidade.rotulo} ajuda={t.criticidade.ajuda} {...register('criticidadeTis')}>
            <option value="">{t.criticidade.naoClassificada}</option>
            {OPCOES_CRITICIDADE.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </option>
            ))}
          </CampoDeSelecao>
          <SugestaoDaIa parecer={parecer} campo="criticidade" valorAtual={valores.criticidadeTis} />
        </div>
      )}

      <div>
        <GrupoDeEscolha
          legenda={t.prazo.rotulo}
          ajuda={t.prazo.ajuda}
          erro={errors.prazo?.message}
          opcoes={OPCOES_PRAZO}
          {...register('prazo')}
        />
        <SugestaoDaIa parecer={parecer} campo="prazo" valorAtual={valores.prazo} />
      </div>

      <div>
        <GrupoDeEscolha
          legenda={t.conformidade.rotulo}
          ajuda={t.conformidade.ajuda}
          erro={errors.conformidade?.message}
          opcoes={OPCOES_CONFORMIDADE}
          {...register('conformidade')}
        />
        <SugestaoDaIa parecer={parecer} campo="conformidade" valorAtual={valores.conformidade} />
      </div>

      <div>
        <GrupoDeEscolha
          legenda={t.aceite.rotulo}
          ajuda={t.aceite.ajuda}
          erro={errors.aceite?.message}
          opcoes={OPCOES_ACEITE}
          {...register('aceite')}
        />
        <SugestaoDaIa parecer={parecer} campo="aceite" valorAtual={valores.aceite} />
      </div>
        </>
      )}

      <CampoDeTexto
        rotulo={t.classificacaoServico.rotulo}
        ajuda={t.classificacaoServico.ajuda}
        erro={errors.classificacaoServico?.message}
        {...register('classificacaoServico')}
      />

      <CampoDeTextoLongo
        rotulo={t.observacao.rotulo}
        ajuda={t.observacao.ajuda}
        erro={errors.observacao?.message}
        rows={3}
        {...register('observacao')}
      />
    </div>
  )
}
